<template>
  <div class="container-fluid">
    <div class="row scale positive">
      <div class="col-md-2 scale-title">Positive</div>
      <div class="col-md-10 scale-choices" :class="{editable: isEditable}">
        <draggable
          class="choice-container"
          group="choices"
          ghost-class="choice-ghost"
          draggable=".draggable"
          v-model="positiveChoices"
        >
          <scale-choice-item
            v-for="choice in positiveChoices"
            :key="choice.id"
            :choice="choice"
            :question="question"
            :class="{draggable: isEditable}"
            :isEditable="isEditable"
            @onUpdateChoice="handleUpdateChoice"
            @onEmpty="handleRemove(choice.id)"
            @onRemove="handleRemove(choice.id)"
          ></scale-choice-item>
          <div slot="footer" v-if="isEditable">
            <div class="placeholder-container">
              Drag choice or
              <a @click="handleCreatePositiveChoice">Create a new choice</a>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <el-divider></el-divider>

    <div class="row scale neutral m-sm">
      <div class="col-md-2 scale-title">Neutral</div>
      <div class="col-md-10 scale-choices" :class="{editable: isEditable}">
        <draggable
          class="choice-container"
          group="choices"
          ghost-class="choice-ghost"
          draggable=".draggable"
          v-model="neutralChoices"
        >
          <scale-choice-item
            v-for="choice in neutralChoices"
            :key="choice.id"
            :choice="choice"
            :question="question"
            :class="{draggable: isEditable}"
            :isEditable="isEditable"
            @onUpdateChoice="handleUpdateChoice"
            @onEmpty="handleRemove(choice.id)"
            @onRemove="handleRemove(choice.id)"
          ></scale-choice-item>
          <div slot="footer" v-if="isEditable">
            <div class="placeholder-container">
              Drag choice or
              <a @click="handleCreateNeutralChoice">Create a new choice</a>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <el-divider></el-divider>

    <div class="row scale negative m-sm">
      <div class="col-md-2 scale-title">Negative</div>
      <div class="col-md-10 scale-choices" :class="{editable: isEditable}">
        <draggable
          class="choice-container"
          group="choices"
          ghost-class="choice-ghost"
          draggable=".draggable"
          v-model="negativeChoices"
        >
          <scale-choice-item
            v-for="choice in negativeChoices"
            :key="choice.id"
            :choice="choice"
            :question="question"
            :class="{draggable: isEditable}"
            :isEditable="isEditable"
            @onUpdateChoice="handleUpdateChoice"
            @onEmpty="handleRemove(choice.id)"
            @onRemove="handleRemove(choice.id)"
          ></scale-choice-item>
          <div slot="footer" v-if="isEditable">
            <div class="placeholder-container">
              Drag choice or
              <a @click="handleCreateNegativeChoice">Create a new choice</a>
            </div>
          </div>
        </draggable>
      </div>
    </div>

    <div v-if="isEditable">
      <el-divider></el-divider>

      <div class="row scale m-sm">
        <div class="col-md-2"></div>
        <div class="col-md-10">
          <el-checkbox label="Present in reverse order" v-model="reverseOrder"></el-checkbox>
        </div>
      </div>
    </div>

    <div class="options">
      <el-button size="mini" @click="handleClear" v-if="isEditable">
        <i class="fa fa-trash"></i> &nbsp; Clear all choices
      </el-button>
      <el-button size="mini" :disabled="!isValid" @click="handleSaveTemplate">
        <i class="fa fa-save"></i> &nbsp; Save as new Choice Template
      </el-button>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import draggable from 'vuedraggable';

import Empty from '@/components/common/Empty';

import { emptyChoice, parseChoices } from '../../utils';
import ScaleChoiceItem from './ScaleChoiceItem';

export default {
  name: 'ScaleEditorExpanded',
  components: {
    draggable,

    Empty,

    ScaleChoiceItem,
  },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    question: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
    isReverseOrder: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    isModified: false,
    calculatedChoices: { positive: [], negative: [], neutral: [] },
    hasSavedTemplate: false,
  }),
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(values) {
        const choices = parseChoices(values);
        this.$emit('input', choices);
      },
    },

    reverseOrder: {
      get() {
        return this.isReverseOrder;
      },

      set(value) {
        this.$emit('onReverseOrder', value);
      },
    },

    positiveChoices: {
      get() {
        return this.value.filter(c => c.polarity > 0);
      },
      set(choices) {
        this.calculatedChoices.positive = choices.map((c, idx) => ({
          ...c,
          polarity: idx + 1,
        }));

        this.debounceAssignCombinedChoices();
      },
    },

    neutralChoices: {
      get() {
        return this.value.filter(c => c.polarity === 0 || !('polarity' in c));
      },
      set(choices) {
        this.calculatedChoices.neutral = choices.map(c => ({ ...c, polarity: 0 }));

        this.debounceAssignCombinedChoices();
      },
    },

    negativeChoices: {
      get() {
        return this.value.filter(c => c.polarity < 0);
      },
      set(choices) {
        this.calculatedChoices.negative = choices.map((c, idx) => ({
          ...c,
          polarity: (choices.length - idx) * -1,
        }));

        this.debounceAssignCombinedChoices();
      },
    },

    isValid() {
      return this.computedValue.length > 0 && this.computedValue.every(c => c !== '');
    },
  },
  beforeDestroy() {
    if (this.isModified && this.computedValue.length > 0 && !this.hasSavedTemplate) {
      this.$confirm(
        'Do you want to save this group of choices to use again in future with other questions?',
        'Save as choice template?',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'info',
        },
      ).then(this.handleSaveTemplate);
    }
  },
  methods: {
    combineChoices() {
      const combined = [
        ...this.calculatedChoices.positive,
        ...this.calculatedChoices.neutral,
        ...this.calculatedChoices.negative,
      ];
      const orderedChoices = combined.map((c, idx) => ({ ...c, order: idx + 1 }));
      return orderedChoices;
    },

    // eslint-disable-next-line
    debounceAssignCombinedChoices: debounce(function() {
      this.computedValue = this.combineChoices();
    }, 1),

    recalculateChoicesFromValue() {
      this.calculatedChoices = {
        positive: this.positiveChoices,
        neutral: this.neutralChoices,
        negative: this.negativeChoices,
      };
    },

    handleRemove(choiceId) {
      const filteredChoices = this.combineChoices({}).filter(c => c.id !== choiceId);
      this.$emit('input', filteredChoices);
    },

    handleClear() {
      this.$emit('input', []);
    },

    handleUpdateChoice(choice) {
      this.isModified = true;
      this.computedValue = this.computedValue.map(c => (c.id === choice.id ? choice : c));
    },

    handleCreatePositiveChoice() {
      this.positiveChoices = [...this.positiveChoices, emptyChoice()];
    },

    handleCreateNeutralChoice() {
      this.neutralChoices = [...this.neutralChoices, emptyChoice()];
    },

    handleCreateNegativeChoice() {
      this.negativeChoices = [...this.negativeChoices, emptyChoice()];
    },

    handleSaveTemplate() {
      this.hasSavedTemplate = true;
      this.$store.dispatch('surveybuilder/choiceTemplateShowCreateDialog', {
        choices: this.computedValue,
        isScale: true,
      });
    },
  },
  watch: {
    value: {
      handler: 'recalculateChoicesFromValue',
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.scale {
  margin: 20px 0;
}
.scale-title {
  text-align: right;
  font-size: 1.1em;
  font-weight: bolder;
}
.scale-choices {
  min-height: 65px;
}
.scale-choices.editable {
  margin-bottom: 30px;
}

.draggable {
  cursor: grab;
}

.choice-container {
  min-height: 65px;
}
.placeholder-container {
  position: absolute;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 2px 2px 2px #efefef;
  font-size: 0.8em;
  color: #ccc;
  min-height: 40px;
  margin: 0;
  width: calc(100% - 30px);
  z-index: 0;
}
.options {
  float: right;
  margin: 10px 12px 0 0;
}
</style>

<style>
.choice-ghost {
  position: relative;
  z-index: 10;
  margin: 6px 2px;
}
</style>



// WEBPACK FOOTER //
// ScaleEditorExpanded.vue?1c5505f8